import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SectorBrandRow } from './api';
import { RISK_HEX, indexHex, integrityRingColor, rankingIntegrityBg } from './palette';

export type RankingColumn = 'fameWeb' | 'fameIndex' | 'fameIntegrity' | 'fameRisk';

/**
 * Intestazioni a due parole e due colori.
 *
 * Il prefisso è "Fame" navy per i tre indici Fame; la prima colonna dal 16/10
 * si chiama "WebScore" e non più "FameWeb", senza il blu, che resta il segno
 * degli indici Fame (feedback, Level 3 punto 1). Il valore interno `fameWeb`
 * non cambia: è una chiave, non un'etichetta.
 */
const COLUMNS: {
  key: RankingColumn;
  /** Etichetta visibile, in due pezzi. */
  label: [string, string];
  prefixColor: string;
  color: string;
}[] = [
  { key: 'fameWeb', label: ['Web', 'Score'], prefixColor: 'var(--muted)', color: '#9B9590' },
  {
    key: 'fameIndex',
    label: ['Fame', 'Index'],
    prefixColor: 'var(--navy)',
    color: 'var(--orange)',
  },
  {
    key: 'fameIntegrity',
    label: ['Fame', 'Integrity'],
    prefixColor: 'var(--navy)',
    color: '#4A9EC5',
  },
  { key: 'fameRisk', label: ['Fame', 'Risk'], prefixColor: 'var(--navy)', color: '#A32D2D' },
];

/**
 * Classifica competitiva del verticale.
 *
 * Colonne in ordine narrativo WebScore, FameIndex, FameIntegrity, FameRisk [v2].
 * L'ordinamento predefinito è per WebScore decrescente, e la colonna attiva
 * viene evidenziata (`col-active`) sia nell'header sia nelle celle.
 */
export function RankingTable({
  brands,
  currentSlug,
  sortBy,
}: {
  brands: SectorBrandRow[];
  currentSlug: string;
  sortBy: RankingColumn;
}): ReactNode {
  const { t } = useTranslation();

  const sorted = [...brands].sort((x, y) => {
    switch (sortBy) {
      case 'fameIntegrity':
        return (y.fameIntegrity ?? 0) - (x.fameIntegrity ?? 0);
      case 'fameIndex':
        // I brand N.D. finiscono in fondo: il riferimento li tratta come -1.
        return (y.fameIndex ?? -1) - (x.fameIndex ?? -1);
      case 'fameRisk':
        return y.fameRiskLevel - x.fameRiskLevel;
      default:
        return y.webScore - x.webScore;
    }
  });

  const cellClass = (col: RankingColumn, base = ''): string =>
    `${base}${col === sortBy ? (base ? ' ' : '') + 'col-active' : ''}`;

  return (
    /* Contenitore di scorrimento. Il riferimento fa scorrere la tabella stessa
       (`.ranking-table { display: block; overflow-x: auto }`), ma su una
       `<table>` quel `display: block` non basta: Firefox le tiene comunque la
       larghezza minima del contenuto, misurata 460 px dentro un contenitore da
       360. Restano cosi' due scroller annidati, e le colonne agganciate — che
       si ancorano a quello interno — possono essere spinte fuori vista
       scorrendo quello esterno. Il `<div>` e' un blocco vero, rispetta la
       larghezza disponibile ed e' l'unico a scorrere. Su desktop non ha stili
       e non cambia nulla. */
    <div className="ranking-scroll">
      <table className="ranking-table">
        <thead>
          <tr>
            {/* Le due colonne di identita' restano agganciate a sinistra mentre
              gli indici scorrono: vedi `.rank-col-*` in app.css. */}
            <th className="rank-col-pos" style={{ width: 40 }}>
              #
            </th>
            <th className="rank-col-name">{t('intelligence.brand')}</th>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className={cellClass(col.key, 'rank-th')}
                data-col={col.key}
                style={{ textAlign: 'center' }}
              >
                <span
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 500,
                    textTransform: 'none',
                    letterSpacing: '-0.3px',
                    fontSize: 12,
                  }}
                >
                  <span style={{ color: col.prefixColor }}>{col.label[0]}</span>
                  <span style={{ color: col.color }}>{col.label[1]}</span>
                </span>
                <span className="rank-arrow">▼</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((brand, i) => {
            const isCurrent = brand.slug === currentSlug;
            return (
              <tr key={brand.slug} className={isCurrent ? 'current-row' : ''}>
                <td className="intel-rank-num rank-col-pos">{i + 1}</td>
                {/* Il nome si tronca con i puntini ([YBD], app.css): il `title`
                    lo restituisce intero al passaggio del mouse. */}
                <td className="intel-rank-name rank-col-name" title={brand.name}>
                  <span className="rank-name-text">{brand.name}</span>
                  {isCurrent ? <span className="arrow">◀</span> : null}
                </td>
                <td className={cellClass('fameWeb', 'intel-rank-num-cell')} data-col="fameWeb">
                  {brand.webScore.toFixed(2)}
                </td>
                <td
                  className={cellClass('fameIndex', 'intel-fi-cell')}
                  data-col="fameIndex"
                  style={{ color: indexHex(brand.fameIndex) }}
                >
                  {brand.fameIndex != null ? (
                    brand.fameIndex.toFixed(2)
                  ) : (
                    <span className="intel-na">{t('integrity.nd')}</span>
                  )}
                </td>
                <td
                  className={cellClass('fameIntegrity')}
                  data-col="fameIntegrity"
                  style={{ textAlign: 'center' }}
                >
                  <span
                    className="intel-ft-pill"
                    style={{
                      background: rankingIntegrityBg(brand.fameIntegrity),
                      color: integrityRingColor(brand.fameIntegrity),
                    }}
                  >
                    {brand.fameIntegrity ?? '—'}%
                  </span>
                </td>
                <td className={cellClass('fameRisk', 'intel-fr-cell')} data-col="fameRisk">
                  <span
                    className="intel-fr-pill"
                    style={{ background: RISK_HEX[brand.fameRiskLevel] }}
                  >
                    <span className="lvl">{brand.fameRiskLevel}</span>
                    {t(`riskLevel.${brand.fameRiskLevel}`)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Selettore di ordinamento, montato nel sottotitolo dell'accordion. */
export function RankingSort({
  value,
  onChange,
}: {
  value: RankingColumn;
  onChange: (value: RankingColumn) => void;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <>
      {t('intelligence.sortBy')}{' '}
      <select
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'inherit',
          color: 'var(--orange)',
          background: 'transparent',
          border: 'none',
          borderBottom: '1px solid var(--orange)',
          cursor: 'pointer',
          padding: '0 2px',
          fontWeight: 600,
        }}
        value={value}
        // L'accordion apre e chiude al click: il selettore non deve propagarlo.
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation();
          onChange(e.target.value as RankingColumn);
        }}
      >
        {/* Attaccato come l'intestazione della colonna, che rende i due pezzi
            senza spazio (feedback 23/10, punto 18). Il valore interno resta
            `fameWeb`: è una chiave, non un'etichetta. */}
        <option value="fameWeb">WebScore</option>
        <option value="fameIndex">FameIndex</option>
        <option value="fameIntegrity">FameIntegrity</option>
        <option value="fameRisk">FameRisk</option>
      </select>
    </>
  );
}
